import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LOGO_URL } from '../data/mockData';
import { resetPassword } from '../services/api';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError('Invalid reset link');
    } else {
      setToken(resetToken);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.newPassword || !form.confirmPassword) {
      setError('Please fill all fields');
      return;
    }

    if (form.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ token, newPassword: form.newPassword });
      setSuccess(true);
      setLoading(false);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#060d06] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/10 border-2 border-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-white text-2xl font-bold mb-2">Password Reset Successful!</h2>
            <p className="text-gray-400 text-sm mb-4">Your password has been changed successfully.</p>
            <p className="text-yellow-400 text-sm">Redirecting to homepage...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060d06] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-yellow-400" />
          <h1 className="text-2xl font-bold text-white">Reset Password</h1>
          <p className="text-gray-500 text-sm">MARSUPILAMI COIN</p>
        </div>

        <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl p-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400 text-sm">
              <XCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-gray-400 text-xs mb-1 block">New Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none pr-10"
                  placeholder="Enter new password (min 6 chars)"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="text-gray-400 text-xs mb-1 block">Confirm New Password</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none"
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 rounded-xl font-bold text-black text-sm uppercase bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-4">
            <button onClick={() => navigate('/')} className="text-yellow-400 hover:text-yellow-300 font-medium">
              Back to Homepage
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
